import { PinoModules, RootLogger } from "verdi-logging";
import { sampleWeightFor, shouldEmitDiagnostics } from "../utils/diagnosticsSampling.js";

// Pinned to info so diagnostics emit regardless of LOG_LEVEL.
const DiagnosticsLogger = RootLogger.child({ module: PinoModules.cachingDiagnostics }, { level: "info" });

/** The rate meaning "emit nothing". The default for a cached action, so diagnostics are opt-in. */
export const DIAGNOSTICS_OFF = 0;

/**
 * Which tier answered a cached-action read, and why. Prefix-structured: `cacheHit*` means the cache
 * answered, `cacheMiss*` means the source did, so a hit-rate metric is a prefix filter.
 */
export type CacheOutcome =
    | "cacheHit"
    | "cacheHitStale"
    /** Source errored or timed out, so the persisted downtime copy was served. */
    | "cacheHitSourceUnavailable"
    | "cacheMiss"
    /** A store read threw, so the read degraded to a source fetch. */
    | "cacheMissCacheUnavailable"
    /** getFromSource() read the source without consulting or populating the cache. */
    | "sourceForced"
    /** No value was produced at all; the read threw. */
    | "failed";

/** Field names the emitter owns, and which a drill-down field therefore may not use. */
type ReservedDiagnosticsField = "actionType" | "outcome" | "durationMs" | "sampleRate" | "sampleWeight";

/** High-cardinality context for Logs Explorer drill-down. Spread flat, but barred from the emitter's own fields. */
export type CacheDrilldown = Record<string, unknown> & Partial<Record<ReservedDiagnosticsField, never>>;

type CacheRead = {
    actionType: string;
    outcome: CacheOutcome;
    /** When the read began, so this owns the duration arithmetic rather than every call site. */
    startedAt: number;
    sampleRate: number;
    drilldown?: CacheDrilldown;
};

/**
 * Emits one info line for a settled read, or nothing if it loses the sampling draw — so call sites state
 * what happened and let this decide whether it is reported. An off or malformed rate never passes the draw.
 *
 * Fields go under `diagnostics`, which feeds GCP log-based metrics. `actionType` and `outcome` are the only
 * low-cardinality vocabularies; ids belong in the drill-down.
 */
export function emitCacheRead({ actionType, outcome, startedAt, sampleRate, drilldown }: CacheRead): void {
    if (!shouldEmitDiagnostics(sampleRate)) return;

    const diagnostics = {
        // Drill-down first so the emitter's own fields win a collision.
        ...drilldown,
        actionType,
        outcome,
        // Clamped so a backwards clock can never emit a negative duration to a metric.
        durationMs: Math.max(0, Date.now() - startedAt),
        sampleRate,
        // Precomputed so a metric that SUMs this reports true counts with no consumer-side scaling.
        sampleWeight: sampleWeightFor(sampleRate),
    };
    DiagnosticsLogger.info({ diagnostics }, `cachedAction ${actionType} ${outcome}`);
}
